import { type ReactNode } from 'react';
import { ARInvoiceDetailSlidein } from '@/components/accounts-receivable/invoice-detail/ar-invoice-detail-slidein';
import { ARAgingDetailTable } from '@/components/accounts-receivable/ARAgingDetailTable';
import type { AgingBasis, InvoiceStatus } from '@erp/rpc';
import type { ArReportState } from './useArReportState';
import { ArReportFilterPanel } from './ArReportFilterPanel';
import { ArReportResultsHeader } from './ArReportResultsHeader';
import { ArReportGroupedTable, ArReportFlatTable } from './ArReportViews';
import { SendStatementDialog } from '@/components/accounts-receivable/statements/BulkSendStatementDialog';

interface ArReportBodyProps {
  state: ArReportState;
  actions?: ReactNode;
}

export function ArReportBody({ state: rs, actions }: ArReportBodyProps) {
  return (
    <>
      <ArReportFilterPanel
        settings={rs.effectiveSettings}
        lockBasis={rs.lockBasis}
        activePreset={rs.activePreset}
        selectedCustomers={rs.selectedCustomers}
        selectedStatuses={rs.selectedStatuses}
        lastSentMinDays={rs.lastSentMinDays}
        lastSentMaxDays={rs.lastSentMaxDays}
        includeNeverSent={rs.includeNeverSent}
        filterPanelOpen={rs.filterPanelOpen}
        onFilterPanelOpenChange={rs.setFilterPanelOpen}
        onPresetClick={rs.handlePresetClick}
        onSettingsChange={rs.handleSettingsChange}
        onCustomersChange={rs.setSelectedCustomers}
        onStatusesChange={rs.setSelectedStatuses}
        onLastSentMinDaysChange={rs.setLastSentMinDays}
        onLastSentMaxDaysChange={rs.setLastSentMaxDays}
        onIncludeNeverSentChange={rs.setIncludeNeverSent}
      />

      <ArReportResultsHeader
        resultCountText={rs.resultCountText}
        viewMode={rs.viewMode}
        onViewModeChange={rs.setViewMode}
        isDownloading={rs.downloadPdfMutation.isPending}
        isLoading={rs.isLoading}
        onDownloadPdf={() =>
          rs.downloadPdfMutation.mutate({
            ...rs.filters,
            viewMode: rs.viewMode,
            ...(rs.viewMode === 'flat' && {
              flatSortBy: rs.flatSortBy,
              flatSortDir: rs.flatSortDir,
            }),
          })
        }
        someSelected={rs.someSelected}
        selectedCount={rs.selectedCustomerIds.size}
        onSendStatements={() => rs.setBulkSendOpen(true)}
        onClearSelection={rs.clearSelection}
        actions={actions}
        syncStatus={rs.lastSyncDisplay}
      />

      {rs.viewMode === 'grouped' && (
        <ArReportGroupedTable
          customers={rs.sortedCustomers}
          totals={rs.totals}
          isLoading={rs.isLoading}
          sortBy={rs.groupedSortBy}
          sortDir={rs.groupedSortDir}
          onSortToggle={rs.handleGroupedSortToggle}
          selectedCustomerIds={rs.selectedCustomerIds}
          allSelected={rs.allSelected}
          someSelected={rs.someSelected}
          toggleSelectAll={rs.toggleSelectAll}
          toggleSelectOne={rs.toggleSelectOne}
          onCustomerClick={rs.handleCustomerClick}
        />
      )}

      {rs.viewMode === 'grouped_detail' && (
        <ARAgingDetailTable
          customers={rs.agingCustomers}
          totals={rs.agingTotals}
          isLoading={rs.agingLoading}
          agingBasis={rs.effectiveSettings.basis as AgingBasis}
          bucketDefinitions={rs.bucketDefinitions}
          statusFilter={
            rs.selectedStatuses.length > 0
              ? rs.selectedStatuses.map((s) => s.id as InvoiceStatus)
              : undefined
          }
          minDays={
            rs.effectiveSettings.minDays > 0
              ? rs.effectiveSettings.minDays
              : undefined
          }
          maxDays={rs.effectiveSettings.maxDays}
        />
      )}

      {rs.viewMode === 'flat' && (
        <ArReportFlatTable
          sortedInvoices={rs.sortedInvoices}
          isLoading={rs.isLoading}
          flatSortBy={rs.flatSortBy}
          flatSortDir={rs.flatSortDir}
          agingBasis={rs.effectiveSettings.basis as AgingBasis}
          onSortToggle={rs.handleFlatSortToggle}
          onInvoiceClick={rs.setSelectedInvoiceId}
        />
      )}

      <ARInvoiceDetailSlidein
        isOpen={rs.selectedInvoiceId !== null}
        onOpenChange={(open) => {
          if (!open) rs.setSelectedInvoiceId(null);
        }}
        invoiceId={rs.selectedInvoiceId ?? ''}
      />

      <SendStatementDialog
        open={rs.bulkSendOpen}
        onOpenChange={(nextOpen) => {
          rs.setBulkSendOpen(nextOpen);
          if (!nextOpen) rs.clearSelection();
        }}
        customers={rs.selectedCustomersForBulk}
        reportSettings={rs.effectiveSettings}
        // Before "Send All", refresh the selected customers' open invoices from
        // Famous so bulk-sent statements reflect the latest source-of-truth.
        refreshBeforeSendAll
      />
    </>
  );
}
